import api from './api';

/**
 * The three tenant-level lead configuration tables.
 *
 * One module rather than three: the lead config screen reads and writes all of
 * them together, and splitting them would mean three imports everywhere for
 * three shapes that never travel apart.
 */

// ---------------------------------------------------------------------------
// Statuses
// ---------------------------------------------------------------------------

export interface LeadStatusConfig {
  id: number;
  key: string;
  title: string;
  sort_order: number;
  /** The status a new lead is given when nothing else says otherwise. */
  is_default: boolean;
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface LeadStatusConfigCreate {
  key: string;
  title: string;
  sort_order?: number;
  is_default?: boolean;
}

export type LeadStatusConfigUpdate = Partial<LeadStatusConfigCreate>;

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

export interface LeadFieldConfig {
  id: number;
  key: string;
  label: string;
  field_type: 'text' | 'bool' | 'date' | 'single_select' | 'number';
  options: { value: string; label: string }[] | null;
  display_in_overview: boolean;
  display_in_edit: boolean;
  display_condition: { field: string; values: string[] } | null;
  default_value: any | null;
  is_required: boolean;
  validation: Record<string, any> | null;
  edit_tab_name: string | null;
  edit_tab_order: number;
  sort_order: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface LeadFieldConfigCreate {
  key: string;
  label: string;
  field_type: string;
  options?: { value: string; label: string }[] | null;
  display_in_overview?: boolean;
  display_in_edit?: boolean;
  display_condition?: { field: string; values: string[] } | null;
  default_value?: any | null;
  is_required?: boolean;
  validation?: Record<string, any> | null;
  edit_tab_name?: string | null;
  edit_tab_order?: number;
  sort_order?: number;
}

export type LeadFieldConfigUpdate = Partial<LeadFieldConfigCreate>;

// ---------------------------------------------------------------------------
// Lead -> patient mapping
// ---------------------------------------------------------------------------

export interface LeadPatientFieldMapping {
  id: number;
  /** A lead field NAME: "first_name" for a built-in, "df_<key>" for one of the
   *  tenant's own. Never an id, so these rows sync between environments. */
  lead_field: string;
  patient_field: string;
  sort_order: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
}

export interface LeadPatientFieldMappingCreate {
  lead_field: string;
  patient_field: string;
  sort_order?: number;
}

export type LeadPatientFieldMappingUpdate = Partial<LeadPatientFieldMappingCreate>;

// ---------------------------------------------------------------------------

function crud<TRow, TCreate, TUpdate>(path: string) {
  return {
    getAll: async (tenantId: number): Promise<TRow[]> => {
      const response = await api.get(path, { params: { tenant_id: tenantId } });
      return response.data;
    },
    create: async (tenantId: number, data: TCreate): Promise<TRow> => {
      const response = await api.post(`${path}?tenant_id=${tenantId}`, data);
      return response.data;
    },
    update: async (id: number, data: TUpdate): Promise<TRow> => {
      const response = await api.put(`${path}${id}`, data);
      return response.data;
    },
    delete: async (id: number): Promise<void> => {
      await api.delete(`${path}${id}`);
    },
  };
}

export const leadStatusConfigService = crud<
  LeadStatusConfig, LeadStatusConfigCreate, LeadStatusConfigUpdate
>('/lead-status-configs/');

export const leadFieldConfigService = crud<
  LeadFieldConfig, LeadFieldConfigCreate, LeadFieldConfigUpdate
>('/lead-field-configs/');

export const leadPatientMappingService = crud<
  LeadPatientFieldMapping, LeadPatientFieldMappingCreate, LeadPatientFieldMappingUpdate
>('/lead-patient-field-mappings/');
